import * as React from "react"
import { FaUser } from "react-icons/fa"
import { gql } from "@apollo/client"
import {
  AspectRatio,
  Avatar,
  Box,
  Center,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Image,
  LinkBox,
  LinkOverlay,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react"
import Head from "next/head"
import { useRouter } from "next/router"

import { nestedPaginate } from "lib/apollo/helpers"
import { useGetProjectQuery } from "lib/graphql"
import { parseUrl } from "lib/helpers/utils"
import { Layout } from "components/Layout"
import { Limiter } from "components/Limiter"
import PdfViewer from "components/PdfViewer"
import { ProjectCollaborators } from "components/ProjectCollaborators"
import { ProjectComments } from "components/ProjectComments"

const _ = gql`
  fragment ProjectDetail on Project {
    id
    title
    description
    pdf
    image
    url
    isSensitiveContent
    privacySetting
    creatorId
    creator {
      id
      avatarUrl
      fullName
      interests
    }
    createdAt
    comments(take: $take, orderBy: $orderBy, where: $where, skip: $skip) {
      items {
        ...CommentItem
      }
      count
    }
    collaborators {
      items {
        ...CollaboratorItem
      }
      count
    }
  }

  query GetProject(
    $id: String!
    $take: Int
    $orderBy: [CommentOrderByWithRelationInput!]
    $where: CommentWhereInput
    $skip: Int
  ) {
    project(id: $id) {
      ...ProjectDetail
    }
  }
`

const TAKE = 10

export default function ProjectDetail() {
  const colorBlackWhite = useColorModeValue("black", "white")
  const router = useRouter()
  const id = router.query.projectId as string

  const { data, loading, fetchMore, refetch } = useGetProjectQuery({
    skip: !!!id,
    variables: { id, take: TAKE },
  })
  const project = data?.project
  const comments = project?.comments.items || []
  const commentsCount = project?.comments.count || 0

  const handleFetchMore = () => {
    if (!project) return
    return fetchMore({
      variables: { skip: project.comments.items.length, take: TAKE },
      updateQuery: nestedPaginate("project", "comments"),
    })
  }

  if (loading)
    return (
      <Center h="100vh" w="100vw">
        <Spinner />
      </Center>
    )
  if (!project)
    return (
      <Center h="100vh" w="100vw">
        Geen project gevonden
      </Center>
    )
  return (
    <>
      <Head>
        <title>Kunstbende - {project?.title}</title>
      </Head>
      <Limiter mt={16} mb={40}>
        <Grid
          templateColumns={{ base: "repeat(1,1fr)", md: "repeat(5, 1fr)" }}
          gap={{ base: 0, md: 32 }}
          pb={120}
          aria-label={`Project detail pagina: ${project.title}`}
        >
          <GridItem colSpan={3}>
            <Flex justifyContent="space-between" w="100%" flexDir={{ base: "column", md: "row" }}>
              <Box w="100%" mb={4}>
                <Heading
                  fontSize={{ base: 32, md: 50 }}
                  fontWeight={900}
                  lineHeight={{ base: "shorter", md: "short" }}
                  px={0}
                  textAlign="left"
                  color={colorBlackWhite}
                  textTransform="uppercase"
                  role="heading"
                  aria-level={1}
                >
                  {project.title}
                </Heading>
                <Text fontSize="lg">{project.description}</Text>
              </Box>
            </Flex>
            <HStack
              spacing={{ base: 0, md: 16 }}
              alignItems="flex-start"
              flexDir={{ base: "column", md: "row" }}
              mt={6}
            >
              <Stack w="100%" spacing={4} mb={4}>
                {project.image && (
                  <Image
                    src={project.image}
                    // h={{ base: "250px", md: "300px" }}
                    objectFit="cover"
                    objectPosition="center"
                    alt="projectbeeld"
                    borderRadius="md"
                  />
                )}
                {project.url && (
                  <AspectRatio
                    _before={{
                      height: 0,
                      content: '""',
                      display: "block",
                      paddingBottom: project.url.includes("soundcloud")
                        ? "25%"
                        : project.url.includes("mixcloud")
                        ? "30%"
                        : "55%",
                    }}
                  >
                    <iframe
                      title={project.title}
                      src={parseUrl(project.url)}
                      allowFullScreen
                      className="iframe-height-restricted"
                      aria-label={`${project.title} video`}
                    />
                  </AspectRatio>
                )}
                {project.pdf && <PdfViewer url={project.pdf} />}
              </Stack>
            </HStack>
          </GridItem>
          <GridItem colSpan={2} mt={{ base: 6, md: 2 }}>
          <LinkBox
              position="relative"
              overflow="hidden"
              p="2px"
              tabIndex={-1} 
              _focusWithin={{
                boxShadow: "0 0 0 3px var(--chakra-colors-blue-300)",
                outline: "none",
                borderRadius: "xl",
              }}
            >
              <LinkOverlay
                href={`/users/${project.creator.id}`}
                textDecoration="none"
                aria-label={`View user ${project.creator.fullName}`}
                tabIndex={0}
              >
                <HStack p={4}>
                  <Avatar
                    src={project.creator.avatarUrl || undefined}
                    name={project.creator.fullName || "avatar"}
                    w="70px"
                    h="70px"
                    bg="gray.600"
                    icon={<FaUser color="gray.900" size={18} />}
                    borderColor="white"
                    borderWidth={2}
                  />
                  <VStack align="left" pl={2} spacing={0}>
                    <Text fontSize="lg">{project.creator.fullName}</Text>
                    <Text color="gray.500" fontSize="md">
                      {project?.creator?.interests &&
                        project.creator.interests.map(
                          (interest, index) =>
                            `${interest}${project.creator.interests.length - 1 !== index ? " / " : ""}`,
                        )}
                    </Text>
                  </VStack>
                </HStack>
              </LinkOverlay>
            </LinkBox>
            {project.collaborators.items.length > 0 && (
              <ProjectCollaborators collaborators={project.collaborators.items} />
            )}
            <ProjectComments
              projectId={project.id}
              comments={comments}
              commentsCount={commentsCount}
              handleFetchMore={handleFetchMore}
              refetch={refetch}
            />
          </GridItem>
        </Grid>
      </Limiter>
    </>
  )
}

ProjectDetail.getLayout = (page: React.ReactElement) => <Layout>{page}</Layout>
